import { FC, useState } from "react";
import { Tree, Tag, Space, Tooltip, Modal, App } from "antd";
import { DataNode } from "antd/es/tree";
import {
  PlusOutlined,
  StopOutlined,
  EditOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { ReimburseCategory } from "@/types/reimburse";
import { CommonStateEnum } from "@/types/common";
import AddReimburse from "./AddReimburse";
import { useTranslation } from "react-i18next";

type FormType = "add" | "update";

interface ReimburseTreeProps {
  treeData: ReimburseCategory[];
  onDataChange: (formType: FormType, result: ReimburseCategory) => void;
  onEnable: (node: ReimburseCategory) => void;
  onDisable: (node: ReimburseCategory) => void;
}

const ReimburseTree: FC<ReimburseTreeProps> = ({
  treeData,
  onDataChange,
  onEnable,
  onDisable,
}) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const [hoveredKey, setHoveredKey] = useState<string>();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<ReimburseCategory>();
  const [formType, setFormType] = useState<FormType>("add");

  const handleAddSub = (node: ReimburseCategory) => {
    setModalData(node);
    setFormType("add");
    setModalOpen(true);
  };

  const handleEdit = (node: ReimburseCategory) => {
    setModalData(node);
    setFormType("update");
    setModalOpen(true);
  };

  const handleDisable = (node: ReimburseCategory) => {
    modal.confirm({
      title: t("reimburse.disableConfirm"),
      content: t("reimburse.disableCascadeTip"),
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
      okButtonProps: { danger: true },
      onOk: () => {
        onDisable(node);
      },
    });
  };

  const handleEnable = (node: ReimburseCategory) => {
    modal.confirm({
      title: t("reimburse.enableConfirm"),
      content: t("reimburse.enableCascadeTip"),
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
      onOk: () => {
        onEnable(node);
      },
    });
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setModalData(undefined);
  };

  const handleModalSuccess = (formType: FormType, result: ReimburseCategory) => {
    onDataChange(formType, result);
    handleModalClose();
  };

  const titleRender = (node: DataNode) => {
    const data = node as unknown as ReimburseCategory;
    const isHovered = hoveredKey === data.id;
    const isActive = data.state === CommonStateEnum.ACTIVE;
    const isUsed = data.useState === CommonStateEnum.ACTIVE;

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          paddingRight: 8,
          // cursor: isActive ? undefined : "not-allowed"
        }}
        onMouseEnter={() => setHoveredKey(data.id)}
        onMouseLeave={() => setHoveredKey(undefined)}
      >
        <Space size={2}>
          <Tag color={isActive ? "blue" : "default"}>{data.code}</Tag>
          <span>{data.name}</span>
        </Space>
        {isHovered && isActive && (
          <Space size={8}>
            <Tooltip title={t("common.edit")}>
              <EditOutlined
                style={{
                  cursor: isUsed ? "not-allowed" : "pointer",
                  color: isUsed ? "#d9d9d9" : "#1890ff",
                  fontSize: 14,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isUsed) handleEdit(data);
                }}
              />
            </Tooltip>
            <Tooltip title={t("common.addSub")}>
              <PlusOutlined
                style={{
                  cursor: "pointer",
                  color: "#52c41a",
                  fontSize: 14,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddSub(data);
                }}
              />
            </Tooltip>
            <Tooltip title={t("common.disable")}>
              <StopOutlined
                style={{
                  cursor: "pointer",
                  color: "#ff4d4f",
                  fontSize: 14,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDisable(data);
                }}
              />
            </Tooltip>
          </Space>
        )}
        {isHovered && !isActive && <Tooltip title={t("org.enable")}>
          <CheckOutlined
            style={{
              cursor: "pointer",
              color: "#52c41a",
              fontSize: 14,
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleEnable(data);
            }}
          />
        </Tooltip>}
      </div>
    );
  };

  return (
    <div style={{ boxSizing: "border-box", padding: 16, width: "100%", maxHeight: "50vh", overflow: "auto" }}>
      <Tree
        selectable={false}
        blockNode
        defaultExpandAll
        treeData={treeData as unknown as DataNode[]}
        fieldNames={{ title: "name", key: "id", children: "subs" }}
        titleRender={titleRender}
        showLine={{ showLeafIcon: false }}
      />
      <AddReimburse
        open={modalOpen}
        data={modalData}
        formType={formType}
        onSuccess={handleModalSuccess}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default ReimburseTree;
